import { ChangeDetectionStrategy, Component, computed, inject } from '@angular/core';
import { DatePipe } from '@angular/common';
import { TranslocoDirective, TranslocoService } from '@jsverse/transloco';

import { LabelBadge } from '../../../shared/components/label-badge/label-badge';
import { ProviderShell } from './provider-shell';
import { LanguageService } from '../../../core/i18n/language.service';
import { discoveryStateColor, discoveryStateKey } from './provider-discovery';

/**
 * Chrome shared by every provider shell: header (name, enabled and discovery
 * badges, meta line, Disable/Enable) and the Feeds | Details tab strip, with the type
 * shell's `<router-outlet>` projected below. Reads everything from the
 * enclosing `ProviderShell`.
 */
@Component({
  selector: 'app-provider-shell-layout',
  standalone: true,
  imports: [TranslocoDirective, LabelBadge, DatePipe],
  templateUrl: './provider-shell-layout.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  // A custom element is display:inline by default, so the template's h-full
  // has no definite height to resolve against and the routed tab content
  // (e.g. the Details tab's sunken background) shrinks to its own content
  // height instead of filling <main> — same fix as Grid's host binding. Every
  // provider shell, tab and feed page repeats this host binding.
  host: { class: 'block h-full min-h-0' },
})
export class ProviderShellLayoutComponent {
  private readonly shell = inject(ProviderShell);
  private readonly languageService = inject(LanguageService);
  private readonly transloco = inject(TranslocoService);

  readonly provider = this.shell.provider;
  readonly feeds = this.shell.feeds;
  readonly tenantCount = this.shell.tenantCount;
  readonly activeTab = this.shell.activeTab;
  readonly discoveryDot = computed(() => discoveryStateColor(this.provider()?.discoveryState ?? null));
  readonly discoveryLabel = computed(() => {
    this.languageService.current(); // re-evaluate on language switch
    return this.transloco.translate(discoveryStateKey(this.provider()?.discoveryState ?? null));
  });

  showTab(tab: 'feeds' | 'details'): void {
    this.shell.showTab(tab);
  }

  toggleEnabled(): void {
    void this.shell.toggleEnabled();
  }
}
